import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { toast } from 'sonner';
import {
    ArrowRight, ShieldCheck, CheckCircle2, ReceiptText, Car,
    Building2, UserRound, Calculator, FileText, BarChart3, Users, Lock
} from 'lucide-react';

const V = { brand: '#6366F1', hover: '#4F46E5' };

const proofPoints = [
    'Calcul TVA sur marge automatique',
    'Export PDF par véhicule',
    'Multi-agences & multi-utilisateurs',
    'Dashboard de performance en temps réel',
];

const steps = [
    { number: '01', title: 'Saisissez vos données', text: "Prix d'achat, prix de vente, frais de remise en état, frais de carte grise… tout ce qui impacte votre marge." },
    { number: '02', title: 'Le calcul est instantané', text: 'TVA sur marge, frais HT, remise client : tout est pris en compte pour afficher votre marge nette réelle.' },
    { number: '03', title: 'Décidez en toute confiance', text: 'Exportez un PDF, suivez vos marges par mois, par commercial et par agence.' },
];

const features = [
    { icon: Calculator, title: 'Calcul précis', text: 'Marge nette HT avec TVA sur marge, frais et remise client intégrés automatiquement.' },
    { icon: FileText, title: 'Export PDF', text: 'Générez un dossier PDF complet pour chaque véhicule, prêt à archiver ou partager.' },
    { icon: BarChart3, title: 'Suivi des performances', text: 'Dashboard avec KPIs, commissions, frais de bureau et SAV par commercial.' },
    { icon: Users, title: 'Multi-agences', text: 'Gérez plusieurs agences avec des règles de commission et des accès différenciés.' },
];

export default function LandingPage() {
    const navigate = useNavigate();
    const [publicReg, setPublicReg] = useState(null);
    const [showAccessForm, setShowAccessForm] = useState(false);
    const [accessForm, setAccessForm] = useState({ nom: '', entreprise: '', email: '', telephone: '', message: '' });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        axios.get(`${API_URL}/api/app-settings/public`).then(r => setPublicReg(r.data.public_registration_enabled)).catch(() => setPublicReg(false));
    }, []);

    const submitAccessRequest = async () => {
        if (!accessForm.nom.trim() || !accessForm.email.trim() || !accessForm.entreprise.trim()) {
            toast.error('Veuillez remplir les champs obligatoires'); return;
        }
        setSending(true);
        try {
            await axios.post(`${API_URL}/api/access-requests`, accessForm);
            toast.success('Votre demande a bien été envoyée !');
            setShowAccessForm(false);
            setAccessForm({ nom: '', entreprise: '', email: '', telephone: '', message: '' });
        } catch { toast.error("Erreur lors de l'envoi"); }
        setSending(false);
    };

    const isBeta = publicReg === false;
    const ctaAction = isBeta ? () => setShowAccessForm(true) : () => navigate('/auth?mode=create-agency');
    const ctaLabel = isBeta ? "Demander un accès" : "Créer mon agence";
    const headerCtaLabel = isBeta ? "Demander un accès" : "Créer mon agence";

    return (
        <div className="min-h-screen bg-white text-gray-900">
            <SEOHead
                title="Marge Auto — Logiciel de calcul de marge pour véhicules d'occasion"
                description="Calculez la marge nette HT de chaque véhicule d'occasion. TVA sur marge, frais, commissions, multi-agences. Essai gratuit 30 jours. Logiciel SaaS pour professionnels automobile."
                path="/landing"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://marge-auto.com/landing" }
                    ]
                }}
            />
            {/* HEADER */}
            <header className="sticky top-0 z-50 border-b border-gray-200 bg-[#F5F7FA]/80 backdrop-blur-sm">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: V.brand }}>
                            <span className="text-base font-bold font-['Inter'] text-gray-900">M</span>
                        </div>
                        <span className="text-lg font-semibold font-['Inter']">Marge Auto</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            onClick={() => navigate('/auth')}
                            className="text-gray-600 hover:text-gray-900 hover:bg-white/10"
                        >
                            Se connecter
                        </Button>
                        <Button
                            onClick={ctaAction}
                            style={{ background: V.brand }}
                            className="text-white hover:opacity-90"
                        >
                            {headerCtaLabel}
                        </Button>
                    </div>
                </div>
            </header>

            <main>
                {/* HERO with cockpit background */}
                <section className="landing-hero-bg border-b border-gray-200">
                    <div className="mx-auto grid min-h-[calc(100vh-65px)] max-w-7xl grid-cols-1 items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
                        <div className="max-w-3xl">
                            <div className="mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm" style={{ border: '1px solid rgba(99,102,241,0.2)', background: 'rgba(99,102,241,0.06)', color: '#818CF8' }}>
                                <ShieldCheck className="h-4 w-4 text-emerald-300" />
                                Professionnels et particuliers
                            </div>
                            <h1 className="font-['Inter'] text-5xl font-bold leading-[1.02] tracking-normal sm:text-6xl lg:text-7xl">
                                Calculez la rentabilité réelle de chaque véhicule.
                            </h1>
                            <p className="mt-6 max-w-2xl text-lg leading-8 text-gray-600">
                                Marge Auto transforme vos prix, frais et TVA en une marge nette HT lisible, fiable et exploitable avant de signer une vente.
                            </p>
                            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                                {isBeta && (
                                    <div className="mb-2 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border border-amber-200 bg-amber-50/80 text-amber-700">
                                        <Lock className="h-3.5 w-3.5" />
                                        Bêta privée — Accès sur invitation
                                    </div>
                                )}
                            </div>
                            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                                <Button
                                    onClick={ctaAction}
                                    className="h-12 px-6 text-base text-white"
                                    style={{ background: V.brand, boxShadow: '0 4px 14px rgba(99,102,241,0.2)' }}
                                    data-testid="landing-cta-register"
                                >
                                    {ctaLabel}
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => navigate('/auth')}
                                    className="h-12 border-white/15 bg-white/5 px-6 text-base text-gray-900 hover:bg-white/10"
                                    data-testid="landing-cta-login"
                                >
                                    Se connecter
                                </Button>
                            </div>
                            <p className="mt-4 text-sm text-gray-500">
                                Essayez gratuitement pendant 30 jours. Toutes les fonctionnalités incluses. Sans engagement.
                            </p>
                            <div className="mt-8 grid max-w-2xl grid-cols-1 gap-3 text-sm text-gray-600 sm:grid-cols-2">
                                {proofPoints.map((point) => (
                                    <div key={point} className="flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                        <span>{point}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Preview card */}
                        <div className="dashboard-preview rounded-xl border border-gray-200 bg-[#F5F7FA]/90 p-4 shadow-2xl shadow-black/50 backdrop-blur-sm">
                            <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-4">
                                <div>
                                    <p className="text-xs uppercase tracking-wider text-gray-400">Dossier véhicule</p>
                                    <p className="mt-1 font-['Inter'] text-xl font-semibold">Peugeot 308 GT Line</p>
                                </div>
                                <span className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1 text-sm text-emerald-300">
                                    Rentable
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <PreviewMetric label="Prix achat" value="14 800 EUR" />
                                <PreviewMetric label="Prix vente" value="18 990 EUR" />
                                <PreviewMetric label="Frais HT" value="1 126 EUR" />
                                <PreviewMetric label="TVA nette" value="572 EUR" />
                            </div>
                            <div className="mt-4 rounded-lg border border-emerald-500/25 bg-emerald-500/10 p-4">
                                <p className="text-xs uppercase tracking-wider text-emerald-200/80">Marge nette HT réelle</p>
                                <p className="mt-2 font-mono text-4xl font-bold text-emerald-300">2 492 EUR</p>
                            </div>
                            <div className="mt-4 space-y-3">
                                <PreviewLine icon={ReceiptText} label="Contrôle technique" value="80 EUR TTC" />
                                <PreviewLine icon={ShieldCheck} label="Garantie" value="390 EUR TTC" />
                                <PreviewLine icon={Car} label="Remise client" value="- 250 EUR" />
                            </div>
                        </div>
                    </div>
                </section>

                {/* HOW IT WORKS */}
                <section className="border-b border-gray-200 bg-white px-4 py-16 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-7xl">
                        <div className="max-w-2xl">
                            <h2 className="font-['Inter'] text-3xl font-bold sm:text-4xl">Comment ça marche</h2>
                            <p className="mt-3 text-gray-500">Trois étapes pour savoir si un véhicule vaut vraiment le coup.</p>
                        </div>
                        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
                            {steps.map((step) => (
                                <article key={step.number} className="rounded-xl border border-gray-200 bg-white p-6">
                                    <p className="font-mono text-sm" style={{ color: V.brand }}>{step.number}</p>
                                    <h3 className="mt-5 font-['Inter'] text-xl font-semibold">{step.title}</h3>
                                    <p className="mt-3 leading-7 text-gray-500">{step.text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                {/* FEATURES */}
                <section className="px-4 py-16 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-7xl">
                        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
                            <div className="max-w-2xl">
                                <h2 className="font-['Inter'] text-3xl font-bold sm:text-4xl">Un outil simple pour vendre avec les bons chiffres</h2>
                                <p className="mt-3 text-gray-500">Pensé pour les dossiers rapides, les décisions d'achat et le suivi quotidien.</p>
                            </div>
                            <Button
                                onClick={ctaAction}
                                className="w-fit text-white"
                                style={{ background: V.brand }}
                            >
                                {isBeta ? 'Demander un accès' : 'Démarrer'}
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </div>
                        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            {features.map((feature) => {
                                const Icon = feature.icon;
                                return (
                                    <article key={feature.title} className="rounded-xl border border-gray-200 bg-white p-6">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white/[0.03]">
                                            <Icon className="h-5 w-5" style={{ color: '#818CF8' }} />
                                        </div>
                                        <h3 className="mt-5 font-['Inter'] text-lg font-semibold">{feature.title}</h3>
                                        <p className="mt-3 leading-7 text-gray-500">{feature.text}</p>
                                    </article>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="border-t border-gray-200 bg-white px-4 py-14 sm:px-6 lg:px-8">
                    <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 md:flex-row md:items-center">
                        <div>
                            <h2 className="font-['Inter'] text-3xl font-bold">Prêt à calculer vos marges ?</h2>
                            <p className="mt-2 text-gray-500">Créez votre espace et gardez chaque dossier véhicule au même endroit.</p>
                        </div>
                        <div className="flex flex-col gap-3 sm:flex-row">
                            <Button
                                onClick={ctaAction}
                                className="text-white"
                                style={{ background: V.brand }}
                            >
                                <Building2 className="mr-2 h-4 w-4" />
                                {ctaLabel}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => navigate('/auth')}
                                className="border-gray-200 text-gray-700 hover:bg-gray-50"
                            >
                                Se connecter
                            </Button>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="border-t border-gray-200 px-4 py-6 text-sm text-gray-400 sm:px-6 lg:px-8">
                <div className="mx-auto flex max-w-7xl flex-col justify-between gap-3 sm:flex-row">
                    <span>&copy; 2026 Marge Auto</span>
                    <div className="flex flex-wrap items-center gap-4">
                        <Link to="/logiciel-marchand-automobile" className="hover:text-gray-700">Logiciel marchand auto</Link>
                        <Link to="/calcul-marge-vehicule-occasion" className="hover:text-gray-700">Calcul de marge VO</Link>
                        <Link to="/gestion-frais-vehicules" className="hover:text-gray-700">Gestion des frais</Link>
                        <Link to="/suivi-marge-par-vehicule" className="hover:text-gray-700">Suivi marge véhicule</Link>
                    </div>
                </div>
            </footer>

            {/* Access Request — Premium Overlay */}
            {showAccessForm && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="access-overlay">
                    {/* Dark overlay */}
                    <div className="absolute inset-0 bg-gray-950/95" onClick={() => setShowAccessForm(false)} />

                    {/* Desktop: centered card | Mobile: near-fullscreen panel */}
                    <div className="relative z-10 w-full max-w-[580px] mx-4
                        sm:mx-auto sm:rounded-2xl
                        max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:mx-0 max-sm:rounded-t-2xl max-sm:rounded-b-none max-sm:max-h-[calc(100dvh-2rem)]
                        bg-gray-950 shadow-2xl shadow-black/40 border border-gray-800/60
                        overflow-y-auto overscroll-contain"
                        style={{ WebkitOverflowScrolling: 'touch' }}
                        data-testid="access-form-panel">

                        {/* Close button */}
                        <button onClick={() => setShowAccessForm(false)}
                            className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-gray-800/80 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                            data-testid="access-close-btn" aria-label="Fermer">
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                        </button>

                        {/* Mobile drag handle */}
                        <div className="sm:hidden flex justify-center pt-3 pb-1">
                            <div className="w-10 h-1 rounded-full bg-gray-700" />
                        </div>

                        {/* Content */}
                        <div className="px-6 sm:px-8 pt-6 sm:pt-8 pb-8 sm:pb-10">
                            {/* Header */}
                            <div className="mb-6 sm:mb-8 pr-8">
                                <div className="flex items-center gap-2.5 mb-3">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#6366F1]">
                                        <span className="text-sm font-bold text-white">M</span>
                                    </div>
                                    <span className="text-sm font-medium text-gray-500 uppercase tracking-wider">Bêta privée</span>
                                </div>
                                <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">Demander un accès</h2>
                                <p className="mt-2 text-sm text-gray-400 leading-relaxed">Marge Auto est actuellement en accès restreint. Remplissez ce formulaire et nous reviendrons vers vous rapidement.</p>
                            </div>

                            {/* Form */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">Nom / Prénom <span className="text-[#6366F1]">*</span></label>
                                    <input type="text" autoComplete="name" value={accessForm.nom} onChange={e => setAccessForm({...accessForm, nom: e.target.value})}
                                        placeholder="Jean Dupont"
                                        className="w-full h-11 px-3.5 rounded-lg bg-gray-900 border border-gray-700/80 text-white placeholder:text-gray-600 text-sm focus:outline-none focus:border-[#6366F1] focus:ring-1 focus:ring-[#6366F1]/40 transition-colors"
                                        data-testid="access-nom" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">Entreprise / Agence <span className="text-[#6366F1]">*</span></label>
                                    <input type="text" autoComplete="organization" value={accessForm.entreprise} onChange={e => setAccessForm({...accessForm, entreprise: e.target.value})}
                                        placeholder="Auto Services SAS"
                                        className="w-full h-11 px-3.5 rounded-lg bg-gray-900 border border-gray-700/80 text-white placeholder:text-gray-600 text-sm focus:outline-none focus:border-[#6366F1] focus:ring-1 focus:ring-[#6366F1]/40 transition-colors"
                                        data-testid="access-entreprise" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">Email <span className="text-[#6366F1]">*</span></label>
                                    <input type="email" autoComplete="email" value={accessForm.email} onChange={e => setAccessForm({...accessForm, email: e.target.value})}
                                        placeholder="jean@monagence.fr"
                                        className="w-full h-11 px-3.5 rounded-lg bg-gray-900 border border-gray-700/80 text-white placeholder:text-gray-600 text-sm focus:outline-none focus:border-[#6366F1] focus:ring-1 focus:ring-[#6366F1]/40 transition-colors"
                                        data-testid="access-email" />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">Téléphone</label>
                                        <input type="tel" autoComplete="tel" value={accessForm.telephone} onChange={e => setAccessForm({...accessForm, telephone: e.target.value})}
                                            placeholder="06 12 34 56 78"
                                            className="w-full h-11 px-3.5 rounded-lg bg-gray-900 border border-gray-700/80 text-white placeholder:text-gray-600 text-sm focus:outline-none focus:border-[#6366F1] focus:ring-1 focus:ring-[#6366F1]/40 transition-colors" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">Message</label>
                                        <input type="text" value={accessForm.message} onChange={e => setAccessForm({...accessForm, message: e.target.value})}
                                            placeholder="Optionnel"
                                            className="w-full h-11 px-3.5 rounded-lg bg-gray-900 border border-gray-700/80 text-white placeholder:text-gray-600 text-sm focus:outline-none focus:border-[#6366F1] focus:ring-1 focus:ring-[#6366F1]/40 transition-colors" />
                                    </div>
                                </div>
                            </div>

                            {/* CTA */}
                            <button onClick={submitAccessRequest} disabled={sending}
                                className="mt-6 w-full h-12 rounded-xl bg-[#6366F1] hover:bg-[#4F46E5] active:bg-[#4338CA] text-white text-sm font-semibold tracking-wide transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                data-testid="submit-access-btn">
                                {sending ? (
                                    <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>Envoi en cours...</>
                                ) : (
                                    <>Demander mon accès<ArrowRight className="w-4 h-4" /></>
                                )}
                            </button>

                            {/* Trust footer */}
                            <p className="mt-4 text-center text-[11px] text-gray-600">Essai gratuit 30 jours — Aucun engagement — Données sécurisées</p>

                            {/* iPhone bottom safe area */}
                            <div className="sm:hidden h-[env(safe-area-inset-bottom,0px)]" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function PreviewMetric({ label, value }) {
    return (
        <div className="rounded-lg border border-gray-200 bg-white/[0.03] p-3">
            <p className="text-xs uppercase tracking-wider text-gray-400">{label}</p>
            <p className="mt-2 font-mono text-lg font-semibold text-gray-900">{value}</p>
        </div>
    );
}

function PreviewLine({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white/[0.03] px-3 py-2">
            <div className="flex items-center gap-2 text-gray-600">
                <Icon className="h-4 w-4 text-gray-400" />
                <span className="text-sm">{label}</span>
            </div>
            <span className="font-mono text-sm text-gray-700">{value}</span>
        </div>
    );
}
